/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import { useEffect } from "react"
import { getDefaultStore } from "jotai"
import { Router } from "../libs/compat/router"
import useSWR from "swr"
import { User } from "../libs/api/backend/src"
import { UserSession } from "../interfaces/userSession"
import { DefaultLoginPageURL, DefaultWelcomePageURL } from "../libs/vars/url"
import { authStorageAdapter } from "../libs/auth/storageAdapter"
import { AuthService } from "../services/authService"
import { KratosSession } from "../libs/kratos/types"
import {
  sessionTokenAtom,
  userProfileAtom,
  authLoadingAtom,
} from "../store/auth"

export const KRATOS_WHOAMI_KEY = "kratos:sessions:whoami"

export interface KratosSessionResult {
  isLoggedIn: boolean
  token: string
  session: KratosSession | null
  user: User | null
  userSession: UserSession
}

const emptyUserSession: UserSession = {
  id: "",
  username: "",
  firstName: "",
  lastName: "",
  patronymic: "",
  isLoggedIn: false,
  token: "",
}

export function buildKratosSessionResult(
  session: KratosSession | null,
  token?: string
): KratosSessionResult {
  if (!session || !session.active) {
    return {
      isLoggedIn: false,
      token: "",
      session: null,
      user: null,
      userSession: { ...emptyUserSession },
    }
  }

  const effectiveToken = token || session.id
  const traits = session.identity?.traits
  const firstName = traits?.firstName || traits?.name?.first || ""
  const lastName = traits?.lastName || traits?.name?.last || ""
  const username = traits?.username || traits?.email || ""
  const id = session.identity?.id || ""

  const userSession: UserSession = {
    id,
    username,
    firstName,
    lastName,
    patronymic: "",
    isLoggedIn: true,
    token: effectiveToken,
  }

  const user: User = {
    id,
    username,
    firstName,
    lastName,
    patronymic: "",
    createdAt: 0,
    updatedAt: 0,
    email: traits?.email,
  }

  return {
    isLoggedIn: true,
    token: effectiveToken,
    session,
    user,
    userSession,
  }
}

export async function fetchKratosWhoami(): Promise<KratosSessionResult> {
  const session = await AuthService.checkSession()
  if (!session || !session.active) {
    try {
      const store = getDefaultStore()
      store.set(sessionTokenAtom, null)
      store.set(userProfileAtom, null)
      store.set(authLoadingAtom, false)
    } catch {
      // ignore
    }
    return buildKratosSessionResult(null)
  }

  const token = (await authStorageAdapter.getToken()) || session.id
  const result = buildKratosSessionResult(session, token)
  const traits = session.identity?.traits
  try {
    const store = getDefaultStore()
    store.set(sessionTokenAtom, token)
    store.set(userProfileAtom, {
      id: session.identity?.id || "",
      email: traits?.email || "",
      username: traits?.username || traits?.email || "",
      role: traits?.role || "seller",
      shopIds: [],
      shops: [],
    })
    store.set(authLoadingAtom, false)
  } catch {
    // ignore
  }
  return result
}

const RedirectIfNotLoginIn = DefaultLoginPageURL
const RedirectIfAlreadyLoginIn = DefaultWelcomePageURL

interface Props {
  isAuthPage?: boolean
  isNeedRedirect?: boolean
}

export function useCheck(props?: Props) {
  const { data, isValidating, mutate, error } = useSWR<KratosSessionResult>(
    KRATOS_WHOAMI_KEY,
    fetchKratosWhoami,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      dedupingInterval: 10000,
    }
  )

  const isLoading = isValidating && !data
  const userSession = data?.userSession || { ...emptyUserSession }

  useEffect(() => {
    if (isLoading || !props?.isNeedRedirect) {
      return
    }

    // Need to check user and if not logged in, redirect to login page
    if (!props?.isAuthPage) {
      if (!data?.isLoggedIn) {
        Router.push(RedirectIfNotLoginIn)
      }
    }

    // Need to check user and if logged in, redirect to main page
    if (props?.isAuthPage) {
      if (data?.isLoggedIn) {
        Router.push(RedirectIfAlreadyLoginIn)
      }
    }
  }, [data?.isLoggedIn, isLoading, props?.isAuthPage, props?.isNeedRedirect])

  return { data: userSession, isLoading, isError: error, mutate }
}

export function useUser() {
  const { data, isValidating, error } = useSWR<KratosSessionResult>(
    KRATOS_WHOAMI_KEY,
    fetchKratosWhoami,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      dedupingInterval: 10000,
    }
  )

  return {
    user: data?.user || null,
    isLoading: isValidating && !data,
    isError: error,
  }
}
